import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Sparkles,
  Award,
  MapPin,
  Star,
  ArrowRight,
  Shield
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const VieraPage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Consistent, detail-oriented cleaning for Viera's modern homes. Weekly or bi-weekly service with the same professional team every visit.",
      link: "/services/house-cleaning"
    },
    {
      title: "Deep Cleaning",
      description: "Thorough, meticulous cleaning for new homes, seasonal refreshes, and move-in preparation. Perfect for Viera's high standards.",
      link: "/services/deep-cleaning"
    },
    {
      title: "Move-In Cleaning",
      description: "Start fresh in your new Viera home with a spotless, sanitized space before unpacking. We handle every detail.",
      link: "/services/move-in-move-out-cleaning"
    }
  ];

  const whyViera = [
    {
      icon: Award,
      title: "Attention to Detail",
      description: "Viera homeowners expect high standards, and we deliver. Heather's 15+ years of executive housekeeping experience means nothing gets overlooked—from baseboards to ceiling fans to every corner of your home."
    },
    {
      icon: Home,
      title: "Respect for Your Investment",
      description: "Your Viera home is a significant investment. We treat it with care and professionalism, using quality products and methods that protect your finishes, fixtures, and furnishings."
    },
    {
      icon: Shield,
      title: "Fully Insured & Trustworthy",
      description: "We're fully licensed and insured, and Bob and Heather personally oversee every job. You're inviting us into your home—we take that responsibility seriously."
    },
    {
      icon: Sparkles,
      title: "Consistent Quality",
      description: "Same trusted cleaners every visit means consistent results. No rotating crews, no surprises. Just reliable, professional cleaning that meets Viera's expectations."
    }
  ];

  const faqs = [
    {
      question: "Do you serve all of Viera, including The Avenues and Duran Golf Club?",
      answer: "Yes. We serve all Viera communities—The Avenues, Duran Golf Club, Viera East, Viera West, and the surrounding neighborhoods. We're familiar with the area and the types of homes here."
    },
    {
      question: "What makes Pierce Premier different from other cleaning companies serving Viera?",
      answer: "We're not a large franchise with rotating crews. Bob and Heather personally oversee every job, and you'll have the same cleaners every visit. That consistency and accountability matter in Viera, where homeowners expect professionalism and attention to detail."
    },
    {
      question: "Can you accommodate flexible schedules for busy professionals?",
      answer: "Absolutely. Many of our Viera clients are professionals with demanding schedules. We work around your availability, and because we're locally owned, we're easier to reach and more flexible than larger companies."
    },
    {
      question: "Do you handle newer homes and modern finishes carefully?",
      answer: "Yes. Heather's experience includes working with high-end finishes, luxury materials, and modern fixtures. We know which products to use on quartz countertops, luxury vinyl plank, stainless steel appliances, and more. Your home is in good hands."
    },
    {
      question: "Are you pet-friendly?",
      answer: "We are! Many of our Viera clients have dogs, cats, and other pets. We'll work around their routines and treat them with care."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Viera, FL | Pierce Premier",
      description: "Premium house cleaning in Viera, FL. Serving The Avenues, Duran, Viera East & West. Same professional team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/viera"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Viera" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Viera",
      description: "Premium residential cleaning services in Viera, FL including recurring house cleaning, deep cleaning, and move-in preparation."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Viera, FL | Pierce Premier"
        description="Premium house cleaning in Viera, FL. The Avenues, Duran, Viera East & West. Same professional team, 15+ years experience, fully insured, detail-oriented. Call 321-408-1347."
        canonical="/areas/viera"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-purple-600" />
                  <Badge className="bg-purple-600 text-white">Premium Service</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Viera, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Viera is home to modern communities, well-maintained neighborhoods, and homeowners who value quality and consistency. Pierce Premier serves Viera with the same level of professionalism and attention to detail that residents here expect from every service provider.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you&apos;re in The Avenues, near Duran Golf Club, or in one of Viera&apos;s growing neighborhoods, you&apos;ll receive the same trusted team every visit, backed by Heather&apos;s 15+ years of executive housekeeping experience. No rotating crews. No surprises. Just consistent, professional cleaning you can rely on.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-purple-600 text-purple-600 px-8 py-4 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80" 
                  alt="Premium house cleaning services in Viera, Florida"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border-4 border-purple-100 hidden lg:block">
                  <div className="flex items-center gap-3">
                    <Award className="w-8 h-8 text-purple-600" />
                    <div>
                      <div className="font-bold text-lg text-gray-900">Detail</div>
                      <div className="text-sm text-gray-600">Oriented</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Viera Homeowners Choose Pierce Premier */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Viera Homeowners Choose Pierce Premier
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Viera residents expect high standards. We meet them with professionalism, consistency, and genuine care.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whyViera.map((reason, idx) => {
                const Icon = reason.icon;
                return (
                  <Card key={idx} className="border-2">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{reason.title}</h3>
                          <p className="text-gray-600 leading-relaxed">{reason.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Services for Viera */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Cleaning Services for Viera Homes
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                From recurring maintenance to deep cleaning, we provide the services Viera homeowners trust.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-purple-200 transition-colors">
                  <CardContent className="p-6">
                    <Sparkles className="w-10 h-10 text-purple-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                    <Link 
                      to={service.link}
                      className="inline-flex items-center text-purple-600 font-semibold hover:text-purple-700"
                    >
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Viera Communities */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Viera Communities We Serve
              </h2>
              <p className="text-gray-600 mb-8">
                We serve homes throughout Viera&apos;s established and growing neighborhoods.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "The Avenues",
                "Duran Golf Club",
                "Viera East",
                "Viera West",
                "Habitat",
                "Sawgrass",
                "Woodside",
                "Turtle Creek",
                "Murrell Road Area"
              ].map((community, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{community}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Codes:</span> 32940, 32955
              </p>
              <p className="text-sm text-gray-500">
                Serving modern homes and families throughout Viera
              </p>
            </div>
          </div>
        </section>

        {/* What Sets Us Apart */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-indigo-50">
          <div className="container mx-auto max-w-5xl">
            <Card className="border-4 border-purple-200 shadow-xl">
              <CardContent className="p-8 md:p-12">
                <Award className="w-12 h-12 text-purple-600 mb-6 mx-auto" />
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  What Sets Pierce Premier Apart in Viera
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <span className="font-semibold">Viera homeowners have options.</span> When you choose Pierce Premier, you&apos;re choosing a locally owned company that values long-term relationships over one-time transactions.
                  </p>
                  <p>
                    Bob and Heather personally oversee every job. You&apos;ll have direct access to the owners—not a corporate call center. You&apos;ll see the same cleaners every visit—not rotating crews who don&apos;t know your home or preferences.
                  </p>
                  <p>
                    <span className="font-semibold">This is the Pierce Premier difference:</span> professional standards, personal accountability, and a genuine commitment to doing the job right. It&apos;s why families in The Avenues, Duran, and across Viera trust us with their homes.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Viera Homeowners
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-purple-200 shadow-xl">
              <CardContent className="p-8 md:p-12 text-center">
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 mx-auto mb-4" />
                <blockquote className="text-xl md:text-2xl text-gray-700 font-medium mb-6 italic">
                  &quot;Best deep cleaning service in Brevard County! They got stains out I thought were permanent. Amazing attention to detail.&quot;
                </blockquote>
                <p className="text-gray-600 font-semibold">David Chen</p>
                <p className="text-gray-500 text-sm">Viera, FL</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready to Experience Professional Cleaning?"
          description="Join Viera families who trust Pierce Premier for consistent, detail-oriented cleaning. Same team every visit, 15+ years of experience, and the quality you expect."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default VieraPage;
